import { useEffect, useState } from "react";
import { Api, ApiError, type ApiKeyInfo } from "../api.js";
import { Badge, TextInput } from "./ui.js";

export function Keys({ api, note }: { api: Api; note?: string }) {
  const [keys, setKeys] = useState<ApiKeyInfo[] | null>(null);
  const [name, setName] = useState("");
  const [fresh, setFresh] = useState<{ name: string; key: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const load = () =>
    api.keys().then(setKeys, (e) => setError(e instanceof ApiError ? e.message : "Could not load keys."));
  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  const create = async () => {
    setError(null);
    try {
      const k = await api.createKey(name.trim());
      setFresh({ name: k.name, key: k.key });
      setName("");
      setCopied(false);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? (e.issues[0]?.message ?? e.message) : "Could not create the key.");
    }
  };
  const revoke = async (k: ApiKeyInfo) => {
    if (!window.confirm(`Revoke “${k.name}”? Anything using it stops working immediately.`)) return;
    setError(null);
    try {
      await api.revokeKey(k.id);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not revoke the key.");
    }
  };

  return (
    <main className="page">
      <h1>API keys</h1>
      <p>{note ?? "Keys let your own systems and this settings app use your account. Treat them like passwords."}</p>
      {error && <div className="banner warn" role="alert">{error}</div>}
      {fresh && (
        <div className="banner ok" role="alert">
          <strong>Copy your new key “{fresh.name}” now. You will not see it again.</strong>
          <pre className="code" tabIndex={0} aria-label="New API key">{fresh.key}</pre>
          <div className="row">
            <button
              className="btn secondary"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(fresh.key);
                  setCopied(true);
                } catch {
                  /* select manually */
                }
              }}
            >
              {copied ? "Copied" : "Copy key"}
            </button>
            <button className="btn primary" onClick={() => setFresh(null)}>
              I have saved it
            </button>
          </div>
        </div>
      )}
      <form
        className="card"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) void create();
        }}
      >
        <TextInput label="Name for a new key" value={name} onChange={setName} max={60} placeholder="e.g. Website backend" />
        <button className="btn primary" type="submit" disabled={!name.trim()}>
          Create key
        </button>
      </form>
      {keys && (
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Key</th>
              <th>Created</th>
              <th>Status</th>
              <th>
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k.id}>
                <td>
                  {k.name} {k.current && <Badge tone="info">in use now</Badge>}
                </td>
                <td>
                  <code>…{k.hint}</code>
                </td>
                <td>{new Date(k.createdAt).toLocaleDateString()}</td>
                <td>{k.revokedAt ? <Badge tone="bad">Revoked</Badge> : <Badge tone="ok">Active</Badge>}</td>
                <td>
                  {!k.revokedAt && (
                    <button className="btn ghost" onClick={() => void revoke(k)} disabled={k.current} title={k.current ? "This is the key you are signed in with. Create another key first." : undefined} aria-label={`Revoke ${k.name}`}>
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </main>
  );
}
